import * as React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Flex } from "@common/components/Flex";
import { Search } from "@common/components/Search";
import { Button } from "@common/components/Button";

import { Folders } from "./components/folders";
import { Fields } from "./components/fields";
import { QueryCreatingProps } from "./query-creating.types";
import { useQueryCreatingController } from "./query-creating.controller";
import { QueryCreatingConditionsBuilder } from "./components/conditions-builder";

import "./query-creating.styles.scss";

export const QueryCreating: React.FC<QueryCreatingProps> = (props) => {
  const { onClose, onSubmit, withSearch, getId, moduleName, submitText } = props;

  const {
    folders,
    folderFields,
    isFoldersLoading,
    isFolderFieldsLoading,
    selectedFolder,
    searchKeyword,
    selectedFolderFields,
    handleSearchChange,
    handleSelectedFolderChange,
    handleSelectedFolderFieldChange,
    handleFolderFieldDrag,
    queryBuilderConditions,
    handleQueryBuilderChange,
    handleAllConditionsRemove
  } = useQueryCreatingController(props);

  return (
    <Modal
      show
      bsSize="lg"
      bsClass="QueryCreating bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle id={getId?.modalTitle}>{I18n.t("Query")}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Flex>
          <div className="QueryCreating__spreadsheet">
            {withSearch && (
              <Search
                iconProps={{ id: getId?.searchIcon }}
                id={getId?.search}
                withLiveSearch
                value={searchKeyword}
                className="QueryCreating__spreadsheet__search"
                onSearch={handleSearchChange}
                classes={{
                  searchForm: "QueryCreating__spreadsheet__search__form",
                }}
              />
            )}
            <Folders
              selectedFolder={selectedFolder}
              folders={folders}
              onSelectedFolderChange={handleSelectedFolderChange}
              loading={isFoldersLoading}
              className="QueryCreating__folders"
              moduleName={moduleName}
            />
          </div>
          <Flex className="QueryCreating__folder-fields-and-conditions-builder">
            <DndProvider backend={HTML5Backend} options={{ enableTouchEvents: false, enableMouseEvents: true }}>
              <Flex alignItems="flex-end">
                <Fields
                  fields={folderFields}
                  loading={isFolderFieldsLoading}
                  className="QueryCreating__folder-fields"
                  moduleName={moduleName}
                  itemUniqProperty="id"
                  selection={{
                    items: selectedFolderFields,
                    onChange: handleSelectedFolderFieldChange,
                    type: "multiple",
                  }}
                  onDrag={handleFolderFieldDrag}
                />
              </Flex>
              <div className="QueryCreating__conditions-builder">
                <Flex justify="space-between">
                  <h6 className="QueryCreating__conditions-builder__title">{I18n.t("Query")}</h6>
                  <Button onClick={() => onSubmit({ searchKeyword, folder: selectedFolder, folderFields })} id={getId?.submitBtn}>
                    {submitText || I18n.t("Apply")}
                  </Button>
                </Flex>
                <QueryCreatingConditionsBuilder
                  conditions={queryBuilderConditions}
                  moduleName={moduleName}
                  className="QueryCreating__conditions-builder__skip-logic"
                  onChange={handleQueryBuilderChange}
                  onAllConditionsRemove={handleAllConditionsRemove}
                />
              </div>
            </DndProvider>
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button onClick={onClose} id={getId?.cancelBtn}>
            {I18n.t("Cancel")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
